import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { selectProfile } from '@store/profile.reducer'
import { taskTrackerApi } from '../../api/taskTracker.api'
import {
  TaskTrackerBoardAccessMember,
  TaskTrackerBoardAccessMode,
} from '../../store/taskTracker.types'
import {
  getTaskTrackerBoardAccessCache,
  setTaskTrackerBoardAccessCache,
} from '../taskTrackerBoardAccess.cache'
import { isTaskTrackerCurrentUserProjectAdmin } from '../taskTrackerCurrentUserPermissions.utils'
import { useTaskTrackerProjectMembers } from './useTaskTrackerProjectMembers'

const getErrorMessage = (error: unknown, fallback: string) => {
  if (error instanceof Error && error.message) return error.message
  return fallback
}

export const useTaskTrackerBoardAccess = (
  boardId: number | null,
  projectId: number | null,
  enabled: boolean,
) => {
  const currentUserId = useSelector(selectProfile)?.id ?? null
  const cachedAccess =
    boardId != null ? getTaskTrackerBoardAccessCache(boardId) : undefined

  const [accessLoaded, setAccessLoaded] = useState(() => Boolean(cachedAccess))
  const [accessMode, setAccessMode] = useState<TaskTrackerBoardAccessMode>(
    () => cachedAccess?.accessMode ?? 'all_project_members',
  )
  const [selectedMembers, setSelectedMembers] = useState<
    TaskTrackerBoardAccessMember[]
  >(() => cachedAccess?.selectedMembers ?? [])
  const [accessLoading, setAccessLoading] = useState(false)
  const [accessError, setAccessError] = useState<string | null>(null)

  const {
    members: projectMembers,
    roles: projectRoles,
    loading: projectMembersLoading,
    error: projectMembersError,
    reloadMembers,
  } = useTaskTrackerProjectMembers(projectId)

  const isCurrentUserProjectAdmin = useMemo(
    () =>
      isTaskTrackerCurrentUserProjectAdmin(
        projectMembers,
        currentUserId,
        projectRoles,
      ),
    [currentUserId, projectMembers, projectRoles],
  )

  const loadBoardAccess = useCallback(async () => {
    if (boardId == null) return

    setAccessLoading(true)
    setAccessError(null)

    try {
      const settings = await taskTrackerApi.getBoardAccess(boardId)
      setAccessMode(settings.accessMode)
      setSelectedMembers(settings.selectedMembers)
      setTaskTrackerBoardAccessCache(boardId, {
        accessMode: settings.accessMode,
        selectedMembers: settings.selectedMembers,
      })
    } catch (error: unknown) {
      setAccessMode('all_project_members')
      setSelectedMembers([])
      setAccessError(
        getErrorMessage(error, 'Не удалось загрузить доступ к доске'),
      )
    } finally {
      setAccessLoading(false)
      setAccessLoaded(true)
    }
  }, [boardId])

  useEffect(() => {
    if (!enabled || boardId == null) return

    const cached = getTaskTrackerBoardAccessCache(boardId)
    if (cached) {
      setAccessMode(cached.accessMode)
      setSelectedMembers(cached.selectedMembers)
      setAccessLoaded(true)
      return
    }

    void loadBoardAccess()
  }, [boardId, enabled, loadBoardAccess])

  const reload = useCallback(async () => {
    await Promise.all([
      boardId != null ? loadBoardAccess() : Promise.resolve(),
      reloadMembers(),
    ])
  }, [boardId, loadBoardAccess, reloadMembers])

  const effectiveAccessMode: TaskTrackerBoardAccessMode = isCurrentUserProjectAdmin
    ? 'all_project_members'
    : accessMode

  return {
    accessMode,
    effectiveAccessMode,
    selectedMembers,
    projectMembers,
    projectRoles,
    accessLoaded,
    accessLoading,
    accessError,
    projectMembersLoading,
    projectMembersError,
    reload,
  }
}
